import { Button, Drawer, Input, notification, Steps } from "antd";
import { useCallback, useEffect, useRef, useState } from "react";
import { LoadingOutlined } from "@ant-design/icons";
import Step4 from "../upload/Step4";
import ParticipantChooser from "./ParticipantChooser";
import PreviewRoot from "../PreviewRoot";
import axios from "../../utils/axios";
import custom from "../../utils/custom";
import { useHistory } from "react-router-dom";
import { useMutation, useQuery } from "react-query";

const steps = [{ title: "Set participants" }, { title: "Preview" }, { title: "Share" }];

const DraftRoot = ({ params, refetch }) => {
  const [step, setStep] = useState(0);
  const history = useHistory();
  const pageSizeRef = useRef({ w: 595, h: 842 });
  const step4Ref = useRef();
  const [draftInfo, setDraftInfo] = useState(null);
  const [docData, setDocData] = useState({
    docid: 0,
    txn_id: 0,
    file: false,
    parts: [],
    stamps: [],
  });

  const close = () => {
    setStep(0);
    setDraftInfo(null);
    setDocData({
      docid: 0,
      txn_id: 0,
      file: false,
      parts: [],
      stamps: [],
    });

    history.push("/drafts/0/0/0");
  };

  const stepHandler = () => {
    let hash = `/drafts/1/${step}/${params.id}`;

    switch (step) {
      case 0: {
        if (draftInfo?.signers.some((el) => el.msisdn.length === 0))
          notification.warning({
            message: "Please fill the all participants",
            duration: 2,
          });
        else {
          hash = `/drafts/1/${step + 1}/${params.id}`;
          setStep(step + 1);
        }
        break;
      }
      case 1: {
        hash = `/drafts/1/${step + 1}/${params.id}`;
        setStep(step + 1);
        break;
      }
      default:
        createDoc();
    }

    history.push(hash);
  };

  const backhandler = () => {
    let hash = `/drafts/1/${step - 1}/${params.id}`;
    history.push(hash);
    setStep(step - 1);
  };

  const { data, isFetching } = useQuery({
    queryKey: ["get_draft_info", params.id],
    queryFn: () => axios.post("/client/api/doc/template/get", { id: params.id }, { showErrorMessage: true }),
    select: ({ data, success }) => {
      if (success) {
        return data.detail;
      } else {
        return null;
      }
    },
    enabled: params.popfilter === "1",
  });

  useEffect(() => {
    if (data) {
      setDraftInfo(data);
    }
  }, [data]);

  const handlePageSizesLoaded = useCallback((data) => {
    if (data?.w && data?.h) {
      pageSizeRef.current = { w: data.w, h: data.h };
    } else {
      pageSizeRef.current = { w: 595, h: 842 };
    }
  }, []);

  const { data: draftFile, isFetching: fileLoading } = useQuery({
    queryKey: ["get_draft_file", params.id],
    queryFn: () => axios.post("/client/api/doc/template/file/get", { doc_id: draftInfo?.doc_id }, { showErrorMessage: true }),
    select: ({ data, success }) => {
      if (success) {
        let nf = new File([custom.base64tou8(data)], draftInfo?.file_name, {
          type: "application/pdf",
          lastModified: new Date(),
        });

        return nf;
      }
    },
    enabled: !!draftInfo,
  });

  const contactsHandler = (idx, key, value) => {
    let recs = { ...draftInfo };
    recs.signers[idx][key] = value;
    setDraftInfo(recs);
  };

  const { mutate: createDoc, isLoading } = useMutation({
    mutationKey: ["create_doc_from_draft"],
    mutationFn: async () => {
      let ids = draftInfo?.signers.map((el) => el.msisdn);
      if (ids.indexOf("") !== -1) {
        notification.error({
          message: "Please fill the all participants!",
          duration: 3,
        });
        throw new Error("Missing participants");
      }

      const reqFile = new FormData();
      reqFile.append("file", draftFile);

      const { data: createData } = await axios.post("/client/api/doc/create", reqFile, { formData: true });
      const docid = createData?.docid;
      setDocData({ ...docData, docid: docid, txn_id: createData?.txn_id });

      const stamps = draftInfo?.stamps?.map((el) => {
        const signer = draftInfo?.signers.find((p) => p.user_id === el.uid);
        return { ...el, msisdn: signer?.msisdn };
      });

      const res1 = await axios.post("/client/api/doc/signer/add", { docid: docid, recs: draftInfo?.signers }, { showErrorMessage: true });
      if (!res1.success) throw new Error("Failed to add signers");

      const res2 = await axios.post(
        "/client/api/doc/stamp/add",
        {
          docid: docid,
          stamps: stamps,
          pageheight: pageSizeRef.current.h,
          pagewidth: pageSizeRef.current.w,
        },
        { showErrorMessage: true },
      );
      if (!res2.success) throw new Error("Failed to add stamps");

      await step4Ref.current.finish();
      setTimeout(refetch, 1000);
    },
  });

  const renderPage = () => {
    switch (step) {
      case 0:
        return (
          <div className="template_preview">
            <div className="sidebar">
              <span>Draft name</span>
              <Input value={draftInfo?.name} readOnly />
              <span>Participants</span>
              {draftInfo?.signers?.map((el, idx) => (
                <ParticipantChooser
                  data={el}
                  key={idx}
                  onChange={(key, val) => contactsHandler(idx, key, val)}
                  defaults={{
                    email: draftInfo?.signers[idx]?.email,
                    msisdn: draftInfo?.signers[idx]?.msisdn,
                  }}
                />
              ))}
            </div>

            <PreviewRoot file={draftFile} stamps={draftInfo?.stamps || []} locked lockInputs onPageSizesLoaded={handlePageSizesLoaded} />
          </div>
        );
      case 1:
        return (
          <div className="step1_container relafull">
            <div className="step1_detail">
              <div className="step1_user_detail" style={{ marginTop: 20 }}>
                <div className="step1_user_detail_title">{draftInfo?.name} </div>
              </div>
              <div className="parts_scroll_area">
                {draftInfo?.signers?.map((per, i) => {
                  return (
                    <div key={i} className="step1_user_detail" style={{ marginTop: 20 }}>
                      <div
                        className="step1_user_detail_title"
                        style={{
                          background: per.bg_color,
                          textTransform: "capitalize",
                        }}>
                        {per?.actionrole}
                      </div>
                      <div className="step1_user_detail_body">
                        <div className="user_dropdown_name">{per.name}</div>
                        <div className="user_dropdown_mobile">{per.msisdn}</div>
                        <div className="user_dropdown_email" color="1">
                          {per.email}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="step3_body">
              <div className="absfull">
                <PreviewRoot file={draftFile} stamps={draftInfo?.stamps || []} lockInputs locked />
              </div>
            </div>
          </div>
        );
      case 2:
        return <Step4 data={docData} ref={step4Ref} />;
      default:
        return <div>default</div>;
    }
  };

  return (
    <Drawer
      title={
        <div className="drawer_title">
          {step > 0 && <Button onClick={backhandler}>Prev</Button>}
          <Steps current={step} onChange={(e) => setStep(e)} responsive items={steps} />
          <Button loading={isLoading} onClick={() => step < 3 && stepHandler()}>
            {step === 2 ? "Send" : "Next"}
          </Button>
        </div>
      }
      open={params.popfilter === "1"}
      onClose={close}
      placement="bottom"
      height="100vh"
      className="custom_drawer">
      {isLoading ||
        isFetching ||
        (fileLoading && (
          <div className="loader">
            <LoadingOutlined />
          </div>
        ))}
      {renderPage()}
    </Drawer>
  );
};

export default DraftRoot;
